import React, { useState, useEffect, useMemo } from "react";
import PageHeader from "../components/PageHeader";
import { getApiBase, getCarrierEmail } from "../api";

export default function CarrierCertifications() {
  const apiBase = useMemo(() => getApiBase(), []);
  const carrierEmail = useMemo(() => getCarrierEmail(), []);
  const [certifications, setCertifications] = useState([]);
  const [certificationTypes, setCertificationTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({
    certificationType: "",
    certificationName: "",
    issuingAuthority: "",
    certificateNumber: "",
    issueDate: "",
    expiryDate: ""
  });

  useEffect(() => {
    fetchCertificationTypes();
    fetchMyCertifications();
  }, []);

  async function fetchCertificationTypes() {
    try {
      const res = await fetch(`${apiBase}/oilfield/certifications`);
      if (res.ok) {
        const data = await res.json();
        setCertificationTypes(data.certificationTypes || []);
      }
    } catch (e) {
      console.error(e);
    }
  }

  async function fetchMyCertifications() {
    setLoading(true);
    try {
      const res = await fetch(`${apiBase}/oilfield/carrier/certifications`, {
        headers: { "x-carrier-email": carrierEmail }
      });
      if (res.ok) {
        const data = await res.json();
        setCertifications(data.certifications || []);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }

  async function addCertification(e) {
    e.preventDefault();
    try {
      const res = await fetch(`${apiBase}/oilfield/carrier/certifications`, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          "x-carrier-email": carrierEmail 
        },
        body: JSON.stringify(formData)
      });
      if (res.ok) {
        setShowAddForm(false);
        setFormData({
          certificationType: "",
          certificationName: "",
          issuingAuthority: "",
          certificateNumber: "",
          issueDate: "",
          expiryDate: ""
        });
        fetchMyCertifications();
      }
    } catch (e) {
      console.error(e);
    }
  }

  function formatDate(iso) {
    if (!iso) return "—";
    return new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
  }

  function getStatusBadge(cert) {
    if (cert.isExpired) {
      return <span style={{ padding: "4px 10px", background: "#fee2e2", color: "#991b1b", borderRadius: 20, fontSize: 11, fontWeight: 600 }}>EXPIRED</span>;
    }
    if (cert.isExpiringSoon) {
      return <span style={{ padding: "4px 10px", background: "#fef3c7", color: "#92400e", borderRadius: 20, fontSize: 11, fontWeight: 600 }}>EXPIRING SOON</span>;
    }
    return <span style={{ padding: "4px 10px", background: "#d1fae5", color: "#065f46", borderRadius: 20, fontSize: 11, fontWeight: 600 }}>ACTIVE</span>;
  }

  function getCertIcon(type) {
    const icons = {
      H2S: "☠️",
      SAFELAND: "🛡️",
      PEC: "📋",
      RIGPASS: "🏗️",
      TWIC: "🪪",
      HAZMAT: "⚠️",
      TANKER: "🛢️",
      MSHA: "⛏️",
      FIRST_AID: "🏥",
      FORKLIFT: "🚜"
    };
    return icons[type] || "📄";
  }

  const activeCerts = certifications.filter(c => !c.isExpired);
  const expiredCerts = certifications.filter(c => c.isExpired);

  return (
    <div style={{ padding: 24, maxWidth: 1200, margin: "0 auto" }}>
      <PageHeader 
        title="🎓 My Certifications" 
        subtitle="Manage your oilfield safety certifications and endorsements"
      />

      {/* Stats */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16, marginBottom: 24 }}>
        <div style={{ background: "white", borderRadius: 8, border: "1px solid #e5e7eb", padding: 16, textAlign: "center" }}>
          <div style={{ fontSize: 28, fontWeight: 700, color: "#2563eb" }}>{certifications.length}</div>
          <div style={{ fontSize: 12, color: "#6b7280" }}>Total Certifications</div>
        </div>
        <div style={{ background: "white", borderRadius: 8, border: "1px solid #e5e7eb", padding: 16, textAlign: "center" }}>
          <div style={{ fontSize: 28, fontWeight: 700, color: "#059669" }}>{activeCerts.length}</div>
          <div style={{ fontSize: 12, color: "#6b7280" }}>Active</div>
        </div>
        <div style={{ background: "white", borderRadius: 8, border: "1px solid #e5e7eb", padding: 16, textAlign: "center" }}>
          <div style={{ fontSize: 28, fontWeight: 700, color: "#f59e0b" }}>{certifications.filter(c => c.isExpiringSoon).length}</div>
          <div style={{ fontSize: 12, color: "#6b7280" }}>Expiring Soon</div>
        </div>
        <div style={{ background: "white", borderRadius: 8, border: "1px solid #e5e7eb", padding: 16, textAlign: "center" }}>
          <div style={{ fontSize: 28, fontWeight: 700, color: "#dc2626" }}>{expiredCerts.length}</div>
          <div style={{ fontSize: 12, color: "#6b7280" }}>Expired</div>
        </div>
      </div>

      {/* Add Button */}
      <div style={{ marginBottom: 20, display: "flex", justifyContent: "flex-end" }}>
        <button
          onClick={() => setShowAddForm(true)}
          style={{ padding: "10px 20px", background: "#2563eb", color: "white", border: "none", borderRadius: 6, fontWeight: 600, cursor: "pointer" }}
        >
          + Add Certification
        </button>
      </div>

      {/* Certifications List */}
      {loading ? (
        <div style={{ textAlign: "center", padding: 60 }}>Loading certifications...</div>
      ) : certifications.length === 0 ? (
        <div style={{ background: "white", borderRadius: 12, border: "1px solid #e5e7eb", padding: 60, textAlign: "center" }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>🎓</div>
          <h3 style={{ margin: 0, fontWeight: 700 }}>No Certifications Yet</h3>
          <p style={{ color: "#6b7280", marginTop: 8 }}>Add your oilfield certifications to qualify for more loads.</p>
          <button
            onClick={() => setShowAddForm(true)}
            style={{ marginTop: 16, padding: "10px 20px", background: "#2563eb", color: "white", border: "none", borderRadius: 6, fontWeight: 600, cursor: "pointer" }}
          >
            Add Your First Certification
          </button>
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(350px, 1fr))", gap: 16 }}>
          {certifications.map(cert => (
            <div 
              key={cert.id}
              style={{ 
                background: "white", 
                borderRadius: 12, 
                border: cert.isExpired ? "2px solid #dc2626" : cert.isExpiringSoon ? "2px solid #f59e0b" : "1px solid #e5e7eb",
                padding: 20
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", marginBottom: 12 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ fontSize: 28 }}>{getCertIcon(cert.certificationType)}</span>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 16 }}>{cert.certificationType}</div>
                    <div style={{ fontSize: 12, color: "#6b7280" }}>{cert.certificationName || certificationTypes.find(t => t.code === cert.certificationType)?.name}</div>
                  </div>
                </div>
                {getStatusBadge(cert)}
              </div>

              <div style={{ fontSize: 13, color: "#374151" }}>
                {cert.issuingAuthority && (
                  <p style={{ margin: "4px 0" }}><strong>Issuer:</strong> {cert.issuingAuthority}</p>
                )}
                {cert.certificateNumber && (
                  <p style={{ margin: "4px 0" }}><strong>Certificate #:</strong> {cert.certificateNumber}</p>
                )}
                <p style={{ margin: "4px 0" }}><strong>Issued:</strong> {formatDate(cert.issueDate)}</p>
                <p style={{ margin: "4px 0" }}>
                  <strong>Expires:</strong> {formatDate(cert.expiryDate)}
                  {cert.isExpiringSoon && !cert.isExpired && (
                    <span style={{ marginLeft: 8, color: "#f59e0b" }}>⚠️ Renew soon!</span>
                  )}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Required Certifications Info */}
      <div style={{ marginTop: 32, background: "#f0f9ff", border: "1px solid #0ea5e9", borderRadius: 12, padding: 20 }}>
        <h3 style={{ margin: "0 0 16px 0", color: "#0369a1" }}>📋 Common Oilfield Certifications</h3>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))", gap: 12 }}>
          {certificationTypes.map(cert => (
            <div key={cert.code} style={{ background: "white", borderRadius: 8, padding: 12, fontSize: 13 }}>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>{getCertIcon(cert.code)} {cert.name}</div>
              <div style={{ color: "#6b7280", fontSize: 12 }}>{cert.description}</div>
              <div style={{ color: "#0369a1", fontSize: 11, marginTop: 4 }}>Valid: {cert.validity}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Add Certification Modal */}
      {showAddForm && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
          <div style={{ background: "white", borderRadius: 12, padding: 24, width: 500, maxHeight: "90vh", overflow: "auto" }}>
            <h2 style={{ margin: "0 0 20px 0" }}>Add Certification</h2>
            <form onSubmit={addCertification}>
              <div style={{ display: "grid", gap: 16 }}>
                <div>
                  <label style={{ display: "block", marginBottom: 4, fontWeight: 500, fontSize: 14 }}>Certification Type *</label>
                  <select
                    value={formData.certificationType}
                    onChange={e => setFormData({ ...formData, certificationType: e.target.value })}
                    required
                    style={{ width: "100%", padding: 10, borderRadius: 6, border: "1px solid #d1d5db" }}
                  >
                    <option value="">Select certification...</option>
                    {certificationTypes.map(cert => (
                      <option key={cert.code} value={cert.code}>{cert.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label style={{ display: "block", marginBottom: 4, fontWeight: 500, fontSize: 14 }}>Issuing Authority</label>
                  <input
                    type="text"
                    value={formData.issuingAuthority}
                    onChange={e => setFormData({ ...formData, issuingAuthority: e.target.value })}
                    placeholder="e.g., SafeGulf, Energy Safety"
                    style={{ width: "100%", padding: 10, borderRadius: 6, border: "1px solid #d1d5db" }}
                  />
                </div>

                <div>
                  <label style={{ display: "block", marginBottom: 4, fontWeight: 500, fontSize: 14 }}>Certificate Number</label>
                  <input
                    type="text"
                    value={formData.certificateNumber}
                    onChange={e => setFormData({ ...formData, certificateNumber: e.target.value })}
                    placeholder="Certificate/ID number"
                    style={{ width: "100%", padding: 10, borderRadius: 6, border: "1px solid #d1d5db" }}
                  />
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                  <div>
                    <label style={{ display: "block", marginBottom: 4, fontWeight: 500, fontSize: 14 }}>Issue Date</label>
                    <input
                      type="date"
                      value={formData.issueDate}
                      onChange={e => setFormData({ ...formData, issueDate: e.target.value })}
                      style={{ width: "100%", padding: 10, borderRadius: 6, border: "1px solid #d1d5db" }}
                    />
                  </div>
                  <div>
                    <label style={{ display: "block", marginBottom: 4, fontWeight: 500, fontSize: 14 }}>Expiry Date *</label>
                    <input
                      type="date"
                      value={formData.expiryDate}
                      onChange={e => setFormData({ ...formData, expiryDate: e.target.value })}
                      required
                      style={{ width: "100%", padding: 10, borderRadius: 6, border: "1px solid #d1d5db" }}
                    />
                  </div>
                </div>
              </div>

              <div style={{ display: "flex", gap: 12, marginTop: 24, justifyContent: "flex-end" }}>
                <button
                  type="button"
                  onClick={() => setShowAddForm(false)}
                  style={{ padding: "10px 20px", background: "#f3f4f6", border: "1px solid #d1d5db", borderRadius: 6, cursor: "pointer" }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{ padding: "10px 20px", background: "#2563eb", color: "white", border: "none", borderRadius: 6, fontWeight: 600, cursor: "pointer" }}
                >
                  Add Certification
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
